
import * as Sentry from '@sentry/vue'

export default defineNuxtPlugin((nuxtApp) => {
  const {
    clearServiceWorkersCache,
    checkForSkew,
    reloadToLatestDeployment,
    setDeploymentIdCookie,
  } = useSkewProtection()

  const reloadingCachedAssetsBeforePageReload: {
    uri: string
    request: Promise<any>
  }[] = []
  let checkingForSkewPromise: Promise<boolean> | undefined

  const startReloadingCachedAsset = (reloadCachedAssetUri: string) => {
    reloadingCachedAssetsBeforePageReload.push(
      {
        uri: reloadCachedAssetUri,
        request: fetch(reloadCachedAssetUri, { cache: 'reload' }),
      }
    )
    console.info('Reloading cached asset: ' + reloadCachedAssetUri)
  }

  const isAlreadyReloadingCachedAsset = (reloadCachedAssetUri: string) =>
    reloadingCachedAssetsBeforePageReload.some(({ uri }) => uri === reloadCachedAssetUri)

  /**
   * Fix browser caching corrupt chunks by reloading cache
   */
  const cacheReload = async (reloadCachedAssetUri: string) => {
    if (isAlreadyReloadingCachedAsset(reloadCachedAssetUri)) {
      return
    }

    // If already reloading a cache, just add the new cached asset (to be reloaded) to the promises
    if (reloadingCachedAssetsBeforePageReload.length) {
      startReloadingCachedAsset(reloadCachedAssetUri)
      return
    }

    startReloadingCachedAsset(reloadCachedAssetUri)

    // Wait for all promises, including those that might be added during execution
    let previousLength = 0
    while (reloadingCachedAssetsBeforePageReload.length > previousLength) {
      previousLength = reloadingCachedAssetsBeforePageReload.length
      await Promise.all(reloadingCachedAssetsBeforePageReload.map(async ({ request }) => {
        try {
          await request
        } catch {
          //
        }
      }))
    }

    await clearServiceWorkersCache()

    reloadNuxtApp()
  }

  const reloadIfSkewed = async (): Promise<boolean> => {
    if (!checkingForSkewPromise) {
      checkingForSkewPromise = checkForSkew()
    }
    const isSkewed = await checkingForSkewPromise
    if (isSkewed) {
      reloadToLatestDeployment()
    }

    return isSkewed
  }

  nuxtApp.hook('app:error', async (e) => {
    const error = e as Error & {
      statusCode: number
      statusMessage?: string
    }

    if (
      error.statusCode !== 404 ||
      !error.message?.includes('/_nuxt/builds/meta/')
    ) {
      return
    }

    const chunkPathMatch = e.message.match(/"([^"]+)"/)
    if (!chunkPathMatch) {
      Sentry.captureException(e)
      return
    }

    console.info('ChunkError reload (static)', error)

    nuxtApp.$config.public.chunkError = true

    if (await reloadIfSkewed()) {
      return
    }

    const [, chunkPath] = chunkPathMatch
    cacheReload(window.location.origin + chunkPath)
  })

  nuxtApp.hook('app:chunkError', async (e) => {
    console.info('ChunkError reload (chunk)', e.error)

    if (!e.error?.message) {
      return
    }

    const chunkUriMatch = e.error.message.match(/https?:\/\/[^\s)]+/)
    if (!chunkUriMatch) {
      Sentry.captureException(e.error)
      return
    }

    nuxtApp.$config.public.chunkError = true

    if (await reloadIfSkewed()) {
      return
    }

    const [chunkUri] = chunkUriMatch
    cacheReload(chunkUri)
  })

  callAfterDocumentIdle(() => {
    // Set __vdpl cookie to env.VERCEL_DEPLOYMENT_ID on tab focus to keep track of which deployment the user is on
    window.addEventListener('focus', () => setDeploymentIdCookie())
  }, 1000)
})
